import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useLocation, useNavigate } from "react-router";
import { useMutation } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { EyeIcon, EyeOffIcon, KeyRoundIcon, Loader2Icon, TriangleAlertIcon } from "lucide-react";

import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminService } from "@/gen/wabms/admin/v1/admin_pb";
import { adminKeys } from "@/lib/admin-queries";
import { errorMessage, errorTitle, unclaimedViolations, violationFor } from "@/lib/errors";
import { toDate } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";

interface LocationState {
  from?: string;
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const setAuthenticated = useUiStore((s) => s.setAuthenticated);

  const [error, setError] = useState<unknown>(undefined);
  const [reveal, setReveal] = useState(false);

  const login = useMutation(AdminService.method.login);

  const form = useForm({
    defaultValues: { passphrase: "" },
    onSubmit: async ({ value }) => {
      setError(undefined);
      try {
        // The response carries only the expiry; the session itself is an
        // HttpOnly cookie the server sets on this response.
        const res = await login.mutateAsync({ passphrase: value.passphrase });
        // Refresh the boot query rather than clearing the cache: a clear would
        // put App's WhoAmI observer back into `pending`, swap this page for the
        // boot splash, and unmount the form mid-submit.
        await queryClient.invalidateQueries({ queryKey: adminKeys.whoAmI() });
        setAuthenticated(true, toDate(res.expiresAt));
        const from = (location.state as LocationState | null)?.from;
        navigate(from ?? "/accounts", { replace: true });
      } catch (err) {
        setError(err);
      }
    },
  });

  // The server validates the passphrase itself; a violation on that field is
  // rendered inline, anything else goes to the alert.
  const passphraseViolation = violationFor(error, "passphrase");
  const otherViolations = unclaimedViolations(error, ["passphrase"]);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background p-6">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>

      <Card className="w-full max-w-sm">
        <CardHeader>
          <div className="mb-1 flex size-9 items-center justify-center rounded-lg border bg-muted">
            <KeyRoundIcon className="size-4" />
          </div>
          <CardTitle>WhatsApp Batch Message Service</CardTitle>
          <CardDescription>
            Enter the system passphrase configured for this deployment.
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="passphrase"
              validators={{
                onSubmit: ({ value }) =>
                  value.trim().length === 0 ? "A passphrase is required." : undefined,
              }}
            >
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Passphrase</Label>
                  <div className="relative">
                    <Input
                      id={field.name}
                      name={field.name}
                      type={reveal ? "text" : "password"}
                      autoComplete="current-password"
                      autoFocus
                      className="pr-9"
                      aria-invalid={
                        field.state.meta.errors.length > 0 ||
                        error !== undefined ||
                        passphraseViolation !== undefined
                      }
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        setError(undefined);
                        field.handleChange(e.target.value);
                      }}
                    />
                    <button
                      type="button"
                      aria-label={reveal ? "Hide passphrase" : "Show passphrase"}
                      className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground hover:text-foreground"
                      onClick={() => setReveal((v) => !v)}
                    >
                      {reveal ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                    </button>
                  </div>
                  {field.state.meta.errors.length > 0 ? (
                    <p className="text-xs text-destructive">{String(field.state.meta.errors[0])}</p>
                  ) : passphraseViolation ? (
                    <p className="text-xs text-destructive">{passphraseViolation}</p>
                  ) : null}
                </div>
              )}
            </form.Field>

            {error !== undefined ? (
              <Alert variant="destructive">
                <TriangleAlertIcon />
                <AlertTitle>{errorTitle(error, "Sign-in failed")}</AlertTitle>
                <AlertDescription>
                  <p>{errorMessage(error, "That passphrase was rejected.")}</p>
                  {otherViolations.length > 0 ? (
                    <ul className="mt-1 list-outside list-disc space-y-0.5 pl-4">
                      {otherViolations.map((v, i) => (
                        <li key={`${v.field}-${i}`}>
                          <code className="font-mono text-xs">{v.field}</code> — {v.description}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  <p className="mt-1">Repeated failures are rate-limited by the server.</p>
                </AlertDescription>
              </Alert>
            ) : null}

            <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
              {([canSubmit, isSubmitting]) => (
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!canSubmit || isSubmitting || login.isPending}
                >
                  {isSubmitting || login.isPending ? (
                    <Loader2Icon className="animate-spin" />
                  ) : null}
                  Sign in
                </Button>
              )}
            </form.Subscribe>
          </form>
        </CardContent>
      </Card>

      <p className="max-w-sm text-center text-xs text-muted-foreground">
        The passphrase is set in the service configuration under
        <code className="mx-1 font-mono">admin.passphrase</code>
        (or the <code className="mx-1 font-mono">WABMS_ADMIN__PASSPHRASE</code> environment
        variable). It is never stored in this browser.
      </p>
    </div>
  );
}
